import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/close-up-emergency-hospital-building_135735-469.jpg';

function EmergencyHealthResponse() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (match other extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Coordination & Incident Command (ESF-8)',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Lead function:', 'Health department coordinates Emergency Support Function 8 (Public Health & Medical).'],
            ['ICS structure:', 'Planning, Operations, Logistics, and Finance/Admin sections for scalable response.'],
            ['Liaison roles:', 'Embed with EOC/SEOC and hospital coalitions; maintain a common operating picture.'],
          ].map(([k, v], i) => (
            <li key={`coord-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Surveillance, Epidemiology & Case Management',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Syndromic & disease reporting:', 'Monitor ED/urgent-care trends and provider/lab reports for early signals.'],
            ['Contact tracing & follow-up:', 'Identify exposures, provide guidance, and monitor high-risk settings.'],
            ['Outbreak control:', 'Source investigation, cohorting, isolation guidance, and targeted prophylaxis when indicated.'],
          ].map(([k, v], i) => (
            <li key={`epi-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Laboratory, Testing & Reporting',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Public health lab capacity:', 'Specimen intake, prioritization, and surge agreements with partner labs.'],
            ['Provider guidance:', 'Collection, packaging, and transport instructions; turn-around expectations.'],
            ['Result notification:', 'Rapid reporting to cases, providers, and relevant jurisdictions.'],
          ].map(([k, v], i) => (
            <li key={`lab-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Medical Countermeasures (MCM) & POD Operations',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['POD strategies:', 'Fixed, drive-through, mobile, and closed PODs for priority groups or the general public.'],
            ['Throughput & flow:', 'Screening → education/consent → dispensing/vaccination → observation.'],
            ['Cold chain & security:', 'Product integrity, inventory control, and site safety procedures.'],
          ].map(([k, v], i) => (
            <li key={`mcm-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Healthcare System Surge & Patient Movement',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Bed tracking & load balancing:', 'Coordinate diversion status, ICU/ventilator availability, and interfacility transfers.'],
            ['Alternate care sites:', 'Stand-up low-acuity or step-down sites to decompress hospitals if needed.'],
            ['Crisis staffing & supplies:', 'Mutual aid, volunteer registries, and prioritized distribution of PPE and consumables.'],
          ].map(([k, v], i) => (
            <li key={`surge-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Isolation, Quarantine & Infection Prevention',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['IP&C practices:', 'Standard, contact, droplet, and airborne precautions as appropriate.'],
            ['Facility guidance:', 'Cohorting, ventilation, environmental cleaning, and PPE optimization.'],
            ['Home guidance:', 'Caregiver instructions, symptom monitoring, and return-to-work/attendance criteria.'],
          ].map(([k, v], i) => (
            <li key={`ip-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Risk Communication & Community Engagement',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Joint Information Center:', 'Unified messages, rumor control, and multilingual materials.'],
            ['Channels:', 'Web, social, SMS/email, hotlines, and partnerships with schools/faith groups.'],
            ['Plain language & equity:', 'Accessible formats (ASL/CC/large print) and culturally relevant outreach.'],
          ].map(([k, v], i) => (
            <li key={`comm-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Behavioral Health & Responder Support',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Crisis counseling:', 'Short-term support, referrals, and peer groups for survivors and staff.'],
            ['Responder wellness:', 'Fatigue management, critical-incident stress support, and rest cycles.'],
            ['Family assistance:', 'Information, reunification processes, and spiritual/psychological care options.'],
          ].map(([k, v], i) => (
            <li key={`bh-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Supplies, Stockpiles & Logistics',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Cache management:', 'PPE, test kits, antivirals/antibiotics, and vaccination supplies.'],
            ['Receiving & staging:', 'Distribution nodes, last-mile delivery, and burn-rate monitoring.'],
            ['Waste & safety:', 'Sharps, biohazard disposal, and cold-chain integrity checks.'],
          ].map(([k, v], i) => (
            <li key={`log-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Training, Exercises & Readiness',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Tabletops & drills:', 'Clinic/POD flow, data systems, and comms exercises with after-action reports.'],
            ['Credentialing & rosters:', 'Just-in-time training, role cards, and deployment checklists.'],
            ['Continuity plans:', 'COOP/COG to sustain essential health functions during prolonged events.'],
          ].map(([k, v], i) => (
            <li key={`train-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Data, Privacy & Reporting',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Situation reporting:', 'Dashboards for cases, capacity, and resource status; standardized metrics.'],
            ['Privacy:', 'Use minimum necessary data; share only with authorized partners for response.'],
            ['Quality & improvement:', 'Data validation, feedback loops, and iterative corrective actions.'],
          ].map(([k, v], i) => (
            <li key={`data-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Emergency Health Response and Preparedness" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Emergency Health Response and Preparedness</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                We coordinate public health and medical operations before, during, and after emergencies—surveillance, outbreak
                control, medical countermeasures, healthcare surge, and clear risk communication—so communities get rapid,
                equitable protection and care.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We protect health and save lives through coordinated planning, rapid response, and sustained recovery—prioritizing
                the most vulnerable and strengthening readiness for what’s next.
              </p>
            </div>

            <div>
              <Link to="/department/health">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }}/>
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default EmergencyHealthResponse;
